/**
 * React Query hooks for the Decision Feed.
 *
 * Provides data fetching for all 3 tabs + KPI banner.
 */

import { useCallback, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-hot-toast';

import {
  decidePending,
  fetchDecisionContext,
  fetchDecisionFeed,
  fetchDecisionKpis,
  fetchLearnedPatterns,
  fetchPendingDecisions,
  type MergeDecisionSummary,
} from '@/services/decisionFeedService';
import { verdictToastLabel } from '@/components/decisions/verdictToast';
import { extractDetailMessage } from '@/utils/errorFormatting';

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const decisionKeys = {
  all: ['decisions'] as const,
  feed: (page: number, entityType?: string, decidedBy?: string, autoResolved?: boolean) =>
    [...decisionKeys.all, 'feed', { page, entityType, decidedBy, autoResolved }] as const,
  pending: (page: number, entityType?: string) =>
    [...decisionKeys.all, 'pending', { page, entityType }] as const,
  patterns: (page: number, status?: string) =>
    [...decisionKeys.all, 'patterns', { page, status }] as const,
  kpis: () => [...decisionKeys.all, 'kpis'] as const,
  context: (decisionId: string) => [...decisionKeys.all, 'context', decisionId] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useDecisionFeed(
  page = 1,
  entityType?: string,
  decidedBy?: string,
  autoResolved?: boolean,
) {
  return useQuery({
    queryKey: decisionKeys.feed(page, entityType, decidedBy, autoResolved),
    queryFn: () =>
      fetchDecisionFeed({
        page,
        page_size: 20,
        entity_type: entityType,
        decided_by: decidedBy,
        auto_resolved: autoResolved || undefined,
      }),
    staleTime: 30_000,
  });
}

export function usePendingDecisions(page = 1, entityType?: string) {
  return useQuery({
    queryKey: decisionKeys.pending(page, entityType),
    queryFn: () =>
      fetchPendingDecisions({
        page,
        page_size: 20,
        entity_type: entityType,
      }),
    staleTime: 30_000,
  });
}

export function useLearnedPatterns(page = 1, status?: string) {
  return useQuery({
    queryKey: decisionKeys.patterns(page, status),
    queryFn: () =>
      fetchLearnedPatterns({
        page,
        page_size: 20,
        status,
      }),
    staleTime: 60_000,
  });
}

export function useDecisionKpis() {
  return useQuery({
    queryKey: decisionKeys.kpis(),
    queryFn: fetchDecisionKpis,
    staleTime: 60_000,
  });
}

export function useDecisionContext(decisionId: string | null) {
  return useQuery({
    queryKey: decisionKeys.context(decisionId ?? ''),
    queryFn: () => fetchDecisionContext(decisionId!),
    enabled: Boolean(decisionId),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Quick decide (AW-50) — one-click verdicts straight from a list card
// ---------------------------------------------------------------------------

/**
 * One-click pending-decision verdicts from the LIST (AW-50): the same
 * POST /pending/{id}/decide the detail modal makes, shared by every surface
 * that renders pending DecisionCards (Inbox, Entity Decisions). The server
 * fans the verdict out to all sibling requests for the pair.
 */
export function useQuickDecide(onError?: (message: string) => void) {
  const queryClient = useQueryClient();
  const [decidingId, setDecidingId] = useState<string | null>(null);

  const quickDecide = useCallback(
    async (
      decision: MergeDecisionSummary,
      verdict: 'merge' | 'separate',
      reasonCode: string,
    ) => {
      setDecidingId(decision.id);
      try {
        await decidePending(decision.id, { decision: verdict, reason_code: reasonCode });
        toast.success(verdictToastLabel(verdict, reasonCode, decision.sibling_count));
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          // The pending row is already resolved — a prior click landed (e.g. a
          // slow grouped decide that outlived the request) or a sibling card
          // superseded it. The refetch below reconciles the list; not an error.
          // Keep the raw signal in the console so anomalous 404s (stale build,
          // garbage id) stay observable.
          console.warn('decide 404 (already resolved?)', decision.id, err.response.data);
          toast.success('Already resolved — refreshing the queue.');
        } else {
          const message = extractDetailMessage(err, 'Failed to save decision');
          toast.error(message);
          onError?.(message);
        }
      } finally {
        // Refetch on EVERY outcome, not just success: a decide that outlives
        // the request timeout still lands server-side, and only a refetch
        // makes the card reflect reality (the "unresponsive Merge" bug).
        queryClient.invalidateQueries({ queryKey: decisionKeys.all });
        setDecidingId(null);
      }
    },
    [queryClient, onError],
  );

  return { quickDecide, decidingId };
}
